import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, CalendarClock, DollarSign, Clock3, CheckCircle2, RefreshCw, Loader2, FileText, TrendingUp, Send, PieChart, Wallet, Bell, Coins, Globe, Monitor, Users, Trash2, Radio, Link as LinkIcon, MessageCircle, Star, Eye, EyeOff, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AdminInvoiceDialog } from "@/components/AdminInvoiceDialog";
import { PaymentRequestDialog } from "@/components/PaymentRequestDialog";
import { EditBookingDialog } from "@/components/EditBookingDialog";
import { QuoteReplyDialog } from "@/components/QuoteReplyDialog";
import { CruiseScheduleManager } from "@/components/CruiseScheduleManager";
import { DriversManager } from "@/components/DriversManager";
import { BookingCalendar } from "@/components/BookingCalendar";
import { QuickContact, quoteTemplates, bookingTemplates } from "@/components/QuickContact";

const waitLabel = (iso) => {
  const mins = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  const txt = mins < 60 ? `${mins} min` : mins < 1440 ? `${Math.floor(mins / 60)} h` : `${Math.floor(mins / 1440)} day${mins >= 2880 ? "s" : ""}`;
  return { txt, late: mins >= 120 };
};

const STATUS_OPTS = ["pending", "confirmed", "completed", "cancelled"];
const statusColor = {
  pending: "text-amber-300 bg-amber-500/10 border-amber-500/30",
  confirmed: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
  completed: "text-sky-300 bg-sky-500/10 border-sky-500/30",
  cancelled: "text-red-300 bg-red-500/10 border-red-500/30",
};

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [etOnly, setEtOnly] = useState(false);
  const [tipReport, setTipReport] = useState(null);
  const [tipStart, setTipStart] = useState("");
  const [tipEnd, setTipEnd] = useState("");
  const [tipLoading, setTipLoading] = useState(false);
  const [visitors, setVisitors] = useState(null);
  const [visitorDays, setVisitorDays] = useState(30);
  const [visitorLoading, setVisitorLoading] = useState(false);
  const [live, setLive] = useState({ online: 0, today: 0, recent: [] });
  const seenVisitRef = useRef(null);
  const [reviews, setReviews] = useState(null);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [payments, setPayments] = useState(null);
  const [payFilter, setPayFilter] = useState("all");
  const [unseen, setUnseen] = useState({ bookings: 0, quotes: 0, reviews: 0 });
  const [tab, setTab] = useState("insights");
  const prevUnseenRef = useRef(null);

  const loadVisitors = useCallback(async (days) => {
    setVisitorLoading(true);
    try {
      const { data } = await api.get("/admin/visitors", { params: { days: days || visitorDays } });
      setVisitors(data);
    } catch {
      toast.error("Could not load visitor data.");
    } finally {
      setVisitorLoading(false);
    }
  }, [visitorDays]);

  const loadTips = useCallback(async () => {
    setTipLoading(true);
    try {
      const params = {};
      if (tipStart) params.start = tipStart;
      if (tipEnd) params.end = tipEnd;
      const { data } = await api.get("/admin/tips", { params });
      setTipReport(data);
    } catch {
      toast.error("Could not load tips report.");
    } finally {
      setTipLoading(false);
    }
  }, [tipStart, tipEnd]);

  const exportTips = async () => {
    try {
      const params = {};
      if (tipStart) params.start = tipStart;
      if (tipEnd) params.end = tipEnd;
      const res = await api.get("/admin/tips/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `driver-tips-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not export tips.");
    }
  };

  const load = useCallback(async (attempt = 0) => {
    try {
      const [b, s, q] = await Promise.all([
        api.get("/admin/bookings"), api.get("/admin/stats"), api.get("/admin/quote-requests"),
      ]);
      setBookings(b.data); setStats(s.data); setQuotes(q.data);
      toast.dismiss("load-retry");
      setLoading(false);
    } catch (err) {
      if (err.response?.status === 401) { navigate("/admin/login"); return; }
      if (attempt < 12) {
        toast.loading("Website is updating — reconnecting…", { id: "load-retry" });
        setTimeout(() => load(attempt + 1), 10000);
        return;
      }
      toast.error("Could not load bookings. Please tap Refresh.", { id: "load-retry" });
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (admin === false) { navigate("/admin/login"); return; }
    if (admin) load();
  }, [admin, load, navigate]);

  const markSeen = useCallback(async (kind) => {
    setUnseen((u) => ({ ...u, [kind]: 0 }));
    if (prevUnseenRef.current) prevUnseenRef.current[kind] = 0;
    try { await api.post(`/admin/mark-seen/${kind}`); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!admin) return;
    const check = async () => {
      try {
        const { data } = await api.get("/admin/unseen-counts");
        const prev = prevUnseenRef.current;
        if (prev && (data.bookings > prev.bookings || data.quotes > prev.quotes || data.reviews > prev.reviews)) {
          const parts = [];
          if (data.quotes > prev.quotes) parts.push(`${data.quotes - prev.quotes} new quote request(s)`);
          if (data.bookings > prev.bookings) parts.push(`${data.bookings - prev.bookings} new booking(s)`);
          if (data.reviews > prev.reviews) parts.push(`${data.reviews - prev.reviews} new review(s)`);
          toast.success(`New: ${parts.join(", ")}`, { duration: 15000 });
          if (data.reviews > prev.reviews) loadReviews();
        }
        prevUnseenRef.current = data;
        setUnseen(data);
      } catch { /* ignore */ }
    };
    check();
    const t = setInterval(() => { load(); check(); }, 60000);
    return () => clearInterval(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin, load]);

  const PAGE_LABEL = (p) => ({ "/": "Home", "/services": "Services", "/explore": "Explore", "/about": "About", "/contact": "Contact", "/book": "Booking", "/quote": "Request a Quote" }[p] || p);

  useEffect(() => {
    if (!admin) return;
    let stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get("/admin/visitors/live");
        if (stop) return;
        setLive(data);
        const recent = data.recent || [];
        if (seenVisitRef.current === null) {
          seenVisitRef.current = new Set(recent.map((v) => v.id));
        } else {
          const fresh = recent.filter((v) => !seenVisitRef.current.has(v.id));
          fresh.slice(0, 3).reverse().forEach((v) => {
            const where = v.country && v.country !== "Unknown" ? ` · ${v.city ? v.city + ", " : ""}${v.country}` : "";
            toast(`👀 Live visitor on ${PAGE_LABEL(v.path)}${where}`, { description: `${v.device || ""}${v.source ? " · via " + v.source : ""}`, duration: 6000 });
          });
          recent.forEach((v) => seenVisitRef.current.add(v.id));
        }
      } catch { /* ignore */ }
    };
    poll();
    const t = setInterval(poll, 20000);
    return () => { stop = true; clearInterval(t); };
  }, [admin]);

  const markPaid = async (id) => {
    try {
      await api.patch(`/admin/bookings/${id}/mark-paid`);
      setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, payment_status: "paid", status: "confirmed" } : b)));
      setPayments((p) => p ? { ...p, items: p.items.map((it) => it.id === id ? { ...it, is_paid: true, payment_status: "paid", amount_paid: it.amount, balance_due: 0 } : it) } : p);
      toast.success("Marked as paid — receipt emailed.");
      api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
      if (payments) loadPayments();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Update failed.");
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, status } : b)));
      toast.success("Status updated.");
      api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Update failed.");
    }
  };

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  const onBookingSaved = (updated) => {
    if (!updated) return;
    setBookings((prev) => prev.map((b) => (b.id === updated.id ? updated : b)));
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  };

  const waLink = (b) => {
    const digits = String(b.phone || "").replace(/\D/g, "");
    if (!digits) return null;
    const intl = digits.length === 10 ? `1${digits}` : digits;
    const ref = b.id.slice(0, 8).toUpperCase();
    const origin = window.location.origin;
    let msg = `Bridge Taxi — Booking ${ref} confirmed. ${b.description || ""}.`;
    if (b.date || b.time) msg += ` Pickup ${[b.date, b.time].filter(Boolean).join(" ")}.`;
    if (b.pickup) msg += ` From ${b.pickup}.`;
    msg += ` Fare $${(b.amount || 0).toFixed(2)} ${b.currency || "CAD"}.`;
    if (b.payment_method === "online" && (b.balance_due ?? b.amount ?? 0) > 0.01) msg += ` Pay: ${origin}/pay/${b.id}`;
    msg += ` Manage/edit: ${origin}/manage/${b.id}`;
    return `https://wa.me/${intl}?text=${encodeURIComponent(msg)}`;
  };

  const reviewUrl = (b) => `${window.location.origin}/review/${b.id}`;
  const reviewMsg = (b) =>
    `Hi${b.name ? " " + b.name : ""}, thank you for riding with Bridge Taxi${b.driver_name ? ` with ${b.driver_name}` : ""}! We'd love your feedback — please leave us a quick review here: ${reviewUrl(b)}`;
  const reviewWaLink = (b) => {
    const digits = String(b.phone || "").replace(/\D/g, "");
    if (!digits) return null;
    const intl = digits.length === 10 ? `1${digits}` : digits;
    return `https://wa.me/${intl}?text=${encodeURIComponent(reviewMsg(b))}`;
  };
  const reviewSmsLink = (b) => {
    const digits = String(b.phone || "").replace(/\D/g, "");
    if (!digits) return null;
    return `sms:${digits.length === 10 ? "+1" + digits : "+" + digits}?&body=${encodeURIComponent(reviewMsg(b))}`;
  };

  const deleteBooking = async (id, name) => {
    if (!window.confirm(`Delete booking/invoice for ${name || "this customer"}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/bookings/${id}`);
      setBookings((prev) => prev.filter((b) => b.id !== id));
      toast.success("Booking deleted.");
      api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed.");
    }
  };

  const deleteQuote = async (id, name) => {
    if (!window.confirm(`Delete quote request from ${name || "this customer"}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/quote-requests/${id}`);
      setQuotes((prev) => prev.filter((q) => q.id !== id));
      toast.success("Quote request deleted.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed.");
    }
  };

  const onQuoteReplied = (id, data) => {
    setQuotes((prev) => prev.map((q) => q.id === id
      ? { ...q, status: "quoted", quoted_amount: data.amount, quote_booking_id: data.id }
      : q));
  };

  const loadReviews = async () => {
    setReviewsLoading(true);
    try {
      const { data } = await api.get("/admin/reviews");
      setReviews(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not load reviews.");
    } finally {
      setReviewsLoading(false);
    }
  };

  const loadPayments = async () => {
    try {
      const { data } = await api.get("/admin/payments");
      setPayments(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not load payments.");
    }
  };

  const setReviewApproved = async (id, approved) => {
    try {
      await api.patch(`/admin/reviews/${id}`, null, { params: { approved } });
      setReviews((prev) => prev.map((r) => (r.id === id ? { ...r, approved } : r)));
      toast.success(approved ? "Review published." : "Review hidden.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Update failed.");
    }
  };

  const deleteReview = async (id) => {
    if (!window.confirm("Delete this review permanently?")) return;
    try {
      await api.delete(`/admin/reviews/${id}`);
      setReviews((prev) => prev.filter((r) => r.id !== id));
      toast.success("Review deleted.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed.");
    }
  };

  const sendReminders = async () => {
    try {
      const { data } = await api.post("/admin/send-reminders");
      toast.success(`Sent ${data.sent} balance reminder${data.sent === 1 ? "" : "s"} for tomorrow's rides.`);
    } catch {
      toast.error("Could not send reminders.");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/bookings/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `bookings-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Bookings exported.");
    } catch {
      toast.error("Export failed.");
    }
  };

  const downloadBackup = async () => {
    try {
      const res = await api.get("/admin/backup/download", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `bridge-taxi-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded — keep it somewhere safe.");
    } catch {
      toast.error("Could not download backup.");
    }
  };

  const exportPriceList = async () => {
    try {
      const res = await api.get("/admin/services/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "bridge-taxi-price-list.csv";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Price list exported.");
    } catch {
      toast.error("Could not export price list.");
    }
  };

  if (admin === null || (admin && loading)) {
    return <div className="min-h-screen grid place-items-center bg-navy"><Loader2 className="h-8 w-8 text-gold animate-spin" /></div>;
  }

  const cards = [
    { icon: CalendarClock, l: "Total bookings", v: stats?.total ?? 0 },
    { icon: DollarSign, l: "Revenue (paid)", v: `$${(stats?.revenue ?? 0).toFixed(2)}` },
    { icon: Clock3, l: "Pending", v: stats?.pending ?? 0 },
    { icon: CheckCircle2, l: "Confirmed", v: stats?.confirmed ?? 0 },
  ];

  return (
    <div className="min-h-screen bg-navy">
      <header className="border-b border-gold/20 bg-navy-light/80 backdrop-blur-xl relative sm:sticky sm:top-0 z-40">
        <div className="max-w-[1720px] mx-auto px-5 sm:px-10 h-18 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src="/logo.png?v=2" alt="Bridge Taxi" className="h-11 w-11 rounded-full object-cover ring-1 ring-gold/40" />
            <div>
              <div className="font-heading text-lg font-bold text-white leading-none">Admin Dashboard</div>
              <div className="text-xs text-slate-400 mt-0.5">{admin?.email}</div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <div data-testid="live-online-pill" title="Visitors on the site in the last 5 minutes" className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3.5 py-2 text-sm font-semibold text-emerald-300">
              <span className="relative flex h-2.5 w-2.5">
                {live.online > 0 && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />}
                <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${live.online > 0 ? "bg-emerald-400" : "bg-slate-500"}`} />
              </span>
              {live.online} online
            </div>
            <button onClick={() => load()} data-testid="refresh-btn" className="inline-flex items-center gap-2 rounded-full border border-gold/30 px-4 py-2 text-sm text-slate-200 hover:border-gold hover:text-gold transition-colors duration-200"><RefreshCw className="h-4 w-4" /> Refresh</button>
            <button onClick={sendReminders} data-testid="send-reminders-btn" className="inline-flex items-center gap-2 rounded-full border border-gold/30 px-4 py-2 text-sm text-slate-200 hover:border-gold hover:text-gold transition-colors duration-200"><Bell className="h-4 w-4" /> Send reminders</button>
            <button onClick={exportCsv} data-testid="export-csv-btn" className="inline-flex items-center gap-2 rounded-full border border-gold/30 px-4 py-2 text-sm text-slate-200 hover:border-gold hover:text-gold transition-colors duration-200"><FileText className="h-4 w-4" /> Export CSV</button>
            <button onClick={exportPriceList} data-testid="export-pricelist-btn" className="inline-flex items-center gap-2 rounded-full border border-gold/30 px-4 py-2 text-sm text-slate-200 hover:border-gold hover:text-gold transition-colors duration-200"><Coins className="h-4 w-4" /> Price list</button>
            <button onClick={downloadBackup} data-testid="download-backup-btn" className="inline-flex items-center gap-2 rounded-full border border-gold/30 px-4 py-2 text-sm text-slate-200 hover:border-gold hover:text-gold transition-colors duration-200"><Wallet className="h-4 w-4" /> Download backup</button>
            <AdminInvoiceDialog onCreated={load} />
            <PaymentRequestDialog />
            <button onClick={doLogout} data-testid="logout-btn" className="inline-flex items-center gap-2 rounded-full bg-gold px-4 py-2 text-sm font-semibold text-navy hover:bg-gold-light transition-colors duration-200"><LogOut className="h-4 w-4" /> Logout</button>
          </div>
        </div>
      </header>

      <main className="max-w-[1720px] mx-auto px-5 sm:px-10 py-10">
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4 mb-10">
          {cards.map((c, i) => (
            <motion.div key={c.l} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
              className="rounded-sm bg-navy-light border border-gold/20 p-6" data-testid={`stat-${i}`}>
              <c.icon className="h-6 w-6 text-gold mb-3" />
              <div className="font-heading text-3xl font-bold text-white">{c.v}</div>
              <div className="text-sm text-slate-400 mt-1">{c.l}</div>
            </motion.div>
          ))}
        </div>

        {(unseen.quotes > 0 || unseen.bookings > 0) && (
          <div data-testid="unseen-alert" className="mb-6 rounded-sm border border-red-500/40 bg-red-500/10 px-5 py-4 flex flex-wrap items-center gap-3">
            <Bell className="h-5 w-5 text-red-300" />
            <span className="text-red-100 font-semibold">Customers are waiting:</span>
            {unseen.quotes > 0 && <button data-testid="unseen-alert-quotes" onClick={() => { setTab("quotes"); markSeen("quotes"); }} className="rounded-full bg-red-500 px-4 py-1.5 text-sm font-semibold text-white hover:bg-red-400 transition-colors duration-200">{unseen.quotes} new quote request{unseen.quotes > 1 ? "s" : ""}</button>}
            {unseen.bookings > 0 && <button data-testid="unseen-alert-bookings" onClick={() => { setTab("bookings"); markSeen("bookings"); }} className="rounded-full bg-red-500 px-4 py-1.5 text-sm font-semibold text-white hover:bg-red-400 transition-colors duration-200">{unseen.bookings} new booking{unseen.bookings > 1 ? "s" : ""}</button>}
          </div>
        )}
        <Tabs value={tab} onValueChange={(v) => { setTab(v); if (["bookings", "quotes", "reviews"].includes(v) && unseen[v] > 0) markSeen(v); }} className="w-full">
          <TabsList className="bg-navy-light border border-gold/20 mb-6 h-auto flex flex-wrap justify-start gap-1 p-1">
            <TabsTrigger value="insights" data-testid="tab-insights" className="data-[state=active]:bg-gold data-[state=active]:text-navy text-slate-300">Insights</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="tab-bookings" className="data-[state=active]:bg-gold data-[state=active]:text-navy text-slate-300">Bookings ({bookings.length}) {unseen.bookings > 0 && <span data-testid="badge-bookings" className="ml-1.5 rounded-full bg-red-500 px-1.5 text-[11px] font-bold text-white">{unseen.bookings} new</span>}</TabsTrigger>
            <TabsTrigger value="quotes" data-testid="tab-quotes" className="data-[state=active]:bg-gold data-[state=active]:text-navy text-slate-300">Quote Requests ({quotes.length}) {unseen.quotes > 0 && <span data-testid="badge-quotes" className="ml-1.5 rounded-full bg-red-500 px-1.5 text-[11px] font-bold text-white">{unseen.quotes} new</span>}</TabsTrigger>
            <TabsTrigger value="tips" data-testid="tab-tips" onClick={() => { if (!tipReport) loadTips(); }} className="data-[state=active]:bg-gold data-[state=active]:text-navy text-slate-300">Tips</TabsTrigger>
            <TabsTrigger value="visitors" data-testid="tab-visitors" onClick={() => { if (!visitors) loadVisitors(); }} className="data-[state=active]:bg-gold data-[state=active]:text-navy text-slate-300">Visitors</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="tab-reviews" onClick={() => { if (!reviews) loadReviews(); }} className="data-[state=active]:bg-gold data-[state=active]:text-navy text-slate-300">Reviews {unseen.reviews > 0 && <span data-testid="badge-reviews" className="ml-1.5 rounded-full bg-red-500 px-1.5 text-[11px] font-bold text-white">{unseen.reviews} new</span>}</TabsTrigger>
            <TabsTrigger value="payments" data-testid="tab-payments" onClick={() => { if (!payments) loadPayments(); }} className="data-[state=active]:bg-gold data-[state=active]:text-navy text-slate-300">Payments</TabsTrigger>
            <TabsTrigger value="calendar" data-testid="tab-calendar" className="data-[state=active]:bg-gold data-[state=active]:text-navy text-slate-300">Calendar</TabsTrigger>
            <TabsTrigger value="drivers" data-testid="tab-drivers" className="data-[state=active]:bg-gold data-[state=active]:text-navy text-slate-300">Drivers</TabsTrigger>
            <TabsTrigger value="cruise" data-testid="tab-cruise" className="data-[state=active]:bg-gold data-[state=active]:text-navy text-slate-300">Cruise Schedule</TabsTrigger>
          </TabsList>

          <TabsContent value="calendar"><BookingCalendar bookings={bookings} /></TabsContent>
          <TabsContent value="drivers"><DriversManager /></TabsContent>

          <TabsContent value="insights">
            <Insights stats={stats} />
          </TabsContent>

          <TabsContent value="bookings">
        <div className="rounded-sm bg-navy-light border border-gold/20 overflow-hidden">
          <div className="px-6 py-4 border-b border-gold/20 flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-heading text-xl font-bold text-white">All bookings</h2>
            {(() => {
              const etCount = bookings.filter((b) => b.payment_status === "awaiting_etransfer").length;
              return (
                <button onClick={() => setEtOnly((v) => !v)} data-testid="filter-awaiting-etransfer"
                  className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold border transition-colors duration-200 ${etOnly ? "bg-gold text-navy border-gold" : "border-gold/40 text-gold hover:border-gold"}`}>
                  <Send className="h-3.5 w-3.5" /> {etOnly ? "Showing awaiting e-Transfer" : "Awaiting e-Transfer"}
                  <span className={`rounded-full px-1.5 ${etOnly ? "bg-navy/20 text-navy" : "bg-gold/20 text-gold"}`}>{etCount}</span>
                </button>
              );
            })()}
          </div>
          {bookings.length === 0 ? (
            <div className="p-12 text-center text-slate-400" data-testid="no-bookings">No bookings yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="bookings-table">
                <thead>
                  <tr className="text-left text-gold/80 uppercase text-[11px] tracking-wider border-b border-gold/20">
                    <th className="px-6 py-3 font-medium">Customer</th>
                    <th className="px-4 py-3 font-medium">Service</th>
                    <th className="px-4 py-3 font-medium">Date / Time</th>
                    <th className="px-4 py-3 font-medium">Pickup</th>
                    <th className="px-4 py-3 font-medium">Fare</th>
                    <th className="px-4 py-3 font-medium">Balance</th>
                    <th className="px-4 py-3 font-medium">Payment</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.filter((b) => !etOnly || b.payment_status === "awaiting_etransfer").map((b) => (
                    <tr key={b.id} className={`border-b border-gold/5 hover:bg-navy/40 transition-colors duration-150 ${b.payment_status === "awaiting_etransfer" ? "bg-gold/[0.06]" : ""}`} data-testid={`booking-row-${b.id}`}>
                      <td className="px-6 py-4">
                        <div className="text-white font-medium">{b.name}{!b.admin_seen && <span data-testid={`new-tag-booking-${b.id}`} className="ml-2 rounded-sm bg-red-500 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white">New</span>}</div>
                        <div className="text-slate-400 text-xs">{b.phone}</div>
                        <div className="text-slate-500 text-xs">{b.email}</div>
                        <QuickContact phone={b.phone} testid={`booking-contact-${b.id}`} templates={bookingTemplates(b)} />
                      </td>
                      <td className="px-4 py-4 text-slate-300 max-w-[200px]">{b.description}
                        {(b.flight_number || b.luggage || b.checking_bags) && (
                          <div className="text-slate-500 text-xs mt-1">
                            {b.flight_number ? `Flight ${b.flight_number}` : ""}{b.flight_number && (b.luggage || b.checking_bags) ? " · " : ""}
                            {b.luggage ? (b.luggage === "none" ? "No bags" : `${b.luggage} bag(s)`) : ""}{b.checking_bags === "yes" ? " (checked)" : ""}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 text-slate-300 whitespace-nowrap">{b.date}<br /><span className="text-slate-500">{b.time}</span></td>
                      <td className="px-4 py-4 text-slate-300 max-w-[160px]">{b.pickup}{b.direction ? <><br /><span className="text-gold text-xs">{b.direction === "to_airport" ? "→ To airport" : "← From airport"}</span></> : null}{b.dropoff ? <><br /><span className="text-slate-500 text-xs">→ {b.dropoff}</span></> : null}</td>
                      <td className="px-4 py-4 font-semibold text-gold whitespace-nowrap">${b.amount.toFixed(2)}</td>
                      <td className="px-4 py-4 whitespace-nowrap" data-testid={`balance-${b.id}`}>
                        {(b.balance_due ?? b.amount ?? 0) > 0.01 ? (
                          <span className="text-amber-300 font-semibold">${(b.balance_due ?? b.amount).toFixed(2)}</span>
                        ) : (
                          <span className="text-emerald-300 text-xs">Settled</span>
                        )}
                        {b.split?.enabled && <div className="text-slate-500 text-[11px] mt-0.5">Split {b.split.shares_paid}/{b.split.shares}</div>}
                        {(b.tips_total ?? 0) > 0 && <div className="text-gold text-[11px] mt-0.5">Tip ${b.tips_total.toFixed(2)}</div>}
                      </td>
                      <td className="px-4 py-4">
                        {b.payment_status === "awaiting_etransfer" ? (
                          <div className="flex flex-col gap-1.5">
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-gold"><Send className="h-3 w-3" /> Awaiting e-Transfer</span>
                            <button onClick={() => markPaid(b.id)} data-testid={`mark-paid-${b.id}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 px-2.5 py-1 text-[11px] font-semibold hover:bg-emerald-500/25 transition-colors duration-150 w-fit"><CheckCircle2 className="h-3 w-3" /> Mark as paid</button>
                          </div>
                        ) : (
                          <span className={`text-xs ${b.payment_status === "paid" ? "text-emerald-300" : "text-slate-400"}`}>
                            {b.payment_status === "paid" ? "Paid online" : b.payment_method === "online" ? "Unpaid" : "On arrival"}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <Select value={b.status} onValueChange={(v) => updateStatus(b.id, v)}>
                          <SelectTrigger data-testid={`status-select-${b.id}`} className={`h-9 w-[130px] text-xs border ${statusColor[b.status] || "border-gold/20 text-white"}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{STATUS_OPTS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                        </Select>
                        <a href={`/invoice/${b.id}`} target="_blank" rel="noopener noreferrer" data-testid={`invoice-link-${b.id}`}
                          className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-gold hover:underline">
                          <FileText className="h-3 w-3" /> Invoice
                        </a>
                        <div className="flex items-center gap-3 flex-wrap">
                          <EditBookingDialog booking={b} onSaved={onBookingSaved} />
                          {waLink(b) && (
                            <a href={waLink(b)} target="_blank" rel="noopener noreferrer" data-testid={`whatsapp-${b.id}`}
                              className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-300 hover:text-emerald-200">
                              <MessageCircle className="h-3 w-3" /> WhatsApp
                            </a>
                          )}
                          <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/manage/${b.id}`); toast.success("Customer edit link copied!"); }}
                            data-testid={`copy-manage-${b.id}`} className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-slate-300 hover:text-gold">
                            <LinkIcon className="h-3 w-3" /> Edit link
                          </button>
                          {reviewWaLink(b) && (
                            <a href={reviewWaLink(b)} target="_blank" rel="noopener noreferrer" data-testid={`review-wa-${b.id}`}
                              className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-gold hover:text-gold-light">
                              <Star className="h-3 w-3" /> Ask for review
                            </a>
                          )}
                          {reviewSmsLink(b) && (
                            <a href={reviewSmsLink(b)} data-testid={`review-sms-${b.id}`}
                              className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-sky-300 hover:text-sky-200">
                              <MessageSquare className="h-3 w-3" /> Text review
                            </a>
                          )}
                          <button onClick={() => { navigator.clipboard.writeText(reviewUrl(b)); toast.success("Review link copied!"); }}
                            data-testid={`copy-review-${b.id}`} className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-slate-300 hover:text-gold">
                            <LinkIcon className="h-3 w-3" /> Review link
                          </button>
                          {b.email && (
                            <button onClick={async () => {
                              if (!window.confirm(`Send the booking confirmation email again to ${b.email}?`)) return;
                              try { await api.post(`/admin/bookings/${b.id}/resend-confirmation`); toast.success(`Confirmation sent to ${b.email}`); }
                              catch (err) { toast.error(err.response?.data?.detail || "Could not send email."); }
                            }} data-testid={`resend-confirmation-${b.id}`} className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-300 hover:text-emerald-200">
                              <Send className="h-3 w-3" /> Resend confirmation
                            </button>
                          )}
                          <button onClick={() => deleteBooking(b.id, b.name)} data-testid={`delete-booking-${b.id}`}
                            className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-red-400 hover:text-red-300">
                            <Trash2 className="h-3 w-3" /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
          </TabsContent>

          <TabsContent value="quotes">
            <div className="rounded-sm bg-navy-light border border-gold/20 overflow-hidden">
              <div className="px-6 py-4 border-b border-gold/20"><h2 className="font-heading text-xl font-bold text-white">Custom quote requests</h2></div>
              {quotes.length === 0 ? (
                <div className="p-12 text-center text-slate-400" data-testid="no-quotes">No quote requests yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="quotes-table">
                    <thead>
                      <tr className="text-left text-gold/80 uppercase text-[11px] tracking-wider border-b border-gold/20">
                        <th className="px-6 py-3 font-medium">Customer</th>
                        <th className="px-4 py-3 font-medium">Service needed</th>
                        <th className="px-4 py-3 font-medium">Pickup / Destination</th>
                        <th className="px-4 py-3 font-medium">Coverage area</th>
                        <th className="px-4 py-3 font-medium">When / Pax</th>
                        <th className="px-4 py-3 font-medium">Details</th>
                        <th className="px-4 py-3 font-medium">Status</th>
                        <th className="px-4 py-3 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quotes.map((q) => (
                        <tr key={q.id} className="border-b border-gold/5 hover:bg-navy/40 transition-colors duration-150" data-testid={`quote-row-${q.id}`}>
                          <td className="px-6 py-4">
                            <div className="text-white font-medium">{q.name}{!q.admin_seen && <span data-testid={`new-tag-quote-${q.id}`} className="ml-2 rounded-sm bg-red-500 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white">New</span>}</div>
                            {(q.status || "new") === "new" && q.created_at && (() => { const w = waitLabel(q.created_at); return (
                              <div data-testid={`quote-wait-${q.id}`} className={`mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${w.late ? "bg-red-500/20 text-red-300 border border-red-500/40" : "bg-amber-500/15 text-amber-300 border border-amber-500/30"}`}>
                                <Clock3 className="h-3 w-3" /> Waiting {w.txt}{w.late ? " — reply now" : ""}
                              </div>); })()}
                            <div className="text-slate-400 text-xs">{q.phone}</div>
                            <div className="text-slate-500 text-xs">{q.email}</div>
                            <QuickContact phone={q.phone} testid={`quote-contact-${q.id}`} templates={quoteTemplates(q)} />
                          </td>
                          <td className="px-4 py-4 text-slate-300 max-w-[200px]">
                            {q.service_type || <span className="text-slate-500">Unspecified</span>}
                            {q.tour_duration ? <div className="text-xs text-gold/70 mt-1">⏱ {q.tour_duration}</div> : null}
                            {q.agreed_price ? <div className="text-xs text-emerald-300 mt-0.5">Agreed ${Number(q.agreed_price).toFixed(2)}</div> : null}
                          </td>
                          <td className="px-4 py-4 text-slate-300 max-w-[180px]">{q.pickup_location}{q.dropoff_location ? <><br /><span className="text-slate-500 text-xs">→ {q.dropoff_location}</span></> : null}</td>
                          <td className="px-4 py-4 text-slate-300 max-w-[160px]">{q.coverage_area || "—"}</td>
                          <td className="px-4 py-4 text-slate-300 whitespace-nowrap">{q.date || "Flexible"}{q.passengers ? <><br /><span className="text-slate-500 text-xs">{q.passengers}</span></> : null}</td>
                          <td className="px-4 py-4 text-slate-400 max-w-[220px] text-xs">{q.message || "—"}</td>
                          <td className="px-4 py-4 whitespace-nowrap" data-testid={`quote-status-${q.id}`}>
                            {q.status === "quoted" ? (
                              <span className="inline-flex items-center rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-semibold text-emerald-300">
                                Quoted ${Number(q.quoted_amount || 0).toFixed(2)}
                              </span>
                            ) : (
                              <span className="inline-flex items-center rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 text-[11px] font-semibold text-amber-300">New</span>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex flex-col gap-2">
                              <QuoteReplyDialog quote={q} onReplied={onQuoteReplied} />
                              <button onClick={() => deleteQuote(q.id, q.name)} data-testid={`delete-quote-${q.id}`}
                                className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-400 hover:text-red-300">
                                <Trash2 className="h-3 w-3" /> Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>
          <TabsContent value="reviews">
            <div className="rounded-sm bg-navy-light border border-gold/20 overflow-hidden" data-testid="reviews-panel">
              <div className="px-6 py-4 border-b border-gold/20 flex items-center justify-between">
                <h2 className="font-heading text-xl font-bold text-white">Customer reviews</h2>
                <span className="text-xs text-slate-400">Approve a review to publish it on your website.</span>
              </div>
              {reviewsLoading ? (
                <div className="p-12 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
              ) : !reviews || reviews.length === 0 ? (
                <div className="p-12 text-center text-slate-400" data-testid="no-reviews">No reviews yet.</div>
              ) : (
                <div className="divide-y divide-gold/10">
                  {reviews.map((r) => (
                    <div key={r.id} className="px-6 py-4 flex flex-col sm:flex-row sm:items-start gap-3 justify-between" data-testid={`review-admin-${r.id}`}>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="flex items-center gap-0.5 text-gold">
                            {[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`h-4 w-4 ${i <= r.rating ? "fill-gold text-gold" : "text-slate-600"}`} />)}
                          </div>
                          <span className="text-white font-semibold">{r.name}</span>
                          {r.approved
                            ? <span className="text-[10px] uppercase tracking-wider rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-emerald-300">Published</span>
                            : <span className="text-[10px] uppercase tracking-wider rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-amber-300">Pending</span>}
                        </div>
                        {(r.driver_name || r.vehicle) && (
                          <div className="text-xs text-gold/70 mt-1">
                            {r.driver_name ? `Driver: ${r.driver_name}` : ""}{r.driver_name && r.vehicle ? " · " : ""}{r.vehicle ? `Car: ${r.vehicle}` : ""}
                          </div>
                        )}
                        {r.comment && <p className="text-slate-300 text-sm mt-1.5 italic">“{r.comment}”</p>}
                        {r.service_type && <div className="text-xs text-slate-500 mt-1">{r.service_type}</div>}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {r.approved ? (
                          <button onClick={() => setReviewApproved(r.id, false)} data-testid={`hide-review-${r.id}`}
                            className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-300 hover:text-amber-200"><EyeOff className="h-3.5 w-3.5" /> Hide</button>
                        ) : (
                          <button onClick={() => setReviewApproved(r.id, true)} data-testid={`approve-review-${r.id}`}
                            className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-300 hover:text-emerald-200"><CheckCircle2 className="h-3.5 w-3.5" /> Approve</button>
                        )}
                        <button onClick={() => deleteReview(r.id)} data-testid={`delete-review-${r.id}`}
                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-400 hover:text-red-300"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
          <TabsContent value="payments">
            <div className="rounded-sm bg-navy-light border border-gold/20 overflow-hidden" data-testid="payments-panel">
              <div className="px-6 py-4 border-b border-gold/20 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <h2 className="font-heading text-xl font-bold text-white">Payments</h2>
                {payments && (
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-emerald-300" data-testid="pay-collected">Collected: ${payments.collected.toFixed(2)}</span>
                    <span className="text-amber-300" data-testid="pay-outstanding">Outstanding: ${payments.outstanding.toFixed(2)}</span>
                    <select value={payFilter} onChange={(e) => setPayFilter(e.target.value)} data-testid="pay-filter" className="rounded-sm bg-navy border border-gold/20 px-2 py-1 text-white text-xs">
                      <option value="all">All</option>
                      <option value="unpaid">Unpaid</option>
                      <option value="paid">Paid</option>
                    </select>
                  </div>
                )}
              </div>
              {!payments ? (
                <div className="p-12 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
              ) : (() => {
                const rows = payments.items.filter((it) => payFilter === "all" ? true : payFilter === "paid" ? it.is_paid : !it.is_paid);
                return rows.length === 0 ? (
                  <div className="p-12 text-center text-slate-400" data-testid="no-payments">No payments to show.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                      <thead className="text-xs uppercase tracking-wider text-slate-400 border-b border-gold/20">
                        <tr><th className="px-5 py-3">Customer</th><th className="px-5 py-3">Service</th><th className="px-5 py-3">Amount</th><th className="px-5 py-3">Method</th><th className="px-5 py-3">Status</th><th className="px-5 py-3">Actions</th></tr>
                      </thead>
                      <tbody>
                        {rows.map((it) => (
                          <tr key={it.id} className="border-b border-gold/5 hover:bg-navy/40" data-testid={`payment-row-${it.id}`}>
                            <td className="px-5 py-3 text-white font-medium">{it.name}</td>
                            <td className="px-5 py-3 text-slate-400 max-w-[260px] truncate">{it.description}</td>
                            <td className="px-5 py-3 text-slate-200 whitespace-nowrap">${it.amount.toFixed(2)}{it.balance_due > 0.01 && !it.is_paid ? <span className="text-amber-300 text-xs block">${it.balance_due.toFixed(2)} due</span> : null}</td>
                            <td className="px-5 py-3 text-slate-400 whitespace-nowrap">{({ online: "Card", e_transfer: "e-Transfer", pay_later: "On arrival" })[it.payment_method] || it.payment_method}</td>
                            <td className="px-5 py-3">
                              {it.is_paid
                                ? <span className="inline-flex items-center rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-semibold text-emerald-300" data-testid={`pay-status-${it.id}`}>Paid</span>
                                : <span className="inline-flex items-center rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 text-[11px] font-semibold text-amber-300" data-testid={`pay-status-${it.id}`}>{it.payment_method === "pay_later" ? "On arrival" : "Unpaid"}</span>}
                            </td>
                            <td className="px-5 py-3">
                              <div className="flex gap-3 whitespace-nowrap">
                                <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/pay/${it.id}`); toast.success("Pay link copied!"); }} data-testid={`pay-link-${it.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-gold hover:text-gold-light"><LinkIcon className="h-3 w-3" /> Pay link</button>
                                {!it.is_paid && <button onClick={() => markPaid(it.id)} data-testid={`mark-paid-${it.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-300 hover:text-emerald-200"><CheckCircle2 className="h-3 w-3" /> Mark paid</button>}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </div>
          </TabsContent>
          <TabsContent value="cruise">
            <CruiseScheduleManager />
          </TabsContent>
          <TabsContent value="tips">
            <div className="rounded-sm bg-navy-light border border-gold/20 overflow-hidden" data-testid="tips-panel">
              <div className="px-6 py-4 border-b border-gold/20 flex flex-wrap items-center justify-between gap-3">
                <h2 className="font-heading text-xl font-bold text-white">Driver tips report</h2>
                <div className="flex flex-wrap items-end gap-2">
                  <div>
                    <label className="block text-[11px] text-slate-400 mb-1">From (pickup date)</label>
                    <input type="date" value={tipStart} onChange={(e) => setTipStart(e.target.value)} data-testid="tip-start" className="rounded-sm bg-input border border-gold/20 px-3 py-2 text-sm text-white" />
                  </div>
                  <div>
                    <label className="block text-[11px] text-slate-400 mb-1">To</label>
                    <input type="date" value={tipEnd} onChange={(e) => setTipEnd(e.target.value)} data-testid="tip-end" className="rounded-sm bg-input border border-gold/20 px-3 py-2 text-sm text-white" />
                  </div>
                  <button onClick={loadTips} data-testid="tip-apply-btn" className="rounded-full bg-gold px-4 py-2 text-sm font-semibold text-navy hover:bg-gold-light transition-colors duration-200">Apply</button>
                  <button onClick={exportTips} data-testid="tip-export-btn" className="inline-flex items-center gap-1.5 rounded-full border border-gold/40 px-4 py-2 text-sm font-semibold text-gold hover:bg-gold/10 transition-colors duration-200"><FileText className="h-4 w-4" /> Export CSV</button>
                </div>
              </div>
              <div className="px-6 py-4 border-b border-gold/20 flex items-center gap-8">
                <div><div className="text-xs text-slate-400">Total tips</div><div className="font-heading text-2xl font-bold text-gold" data-testid="tip-total">${(tipReport?.total ?? 0).toFixed(2)}</div></div>
                <div><div className="text-xs text-slate-400">Tipped bookings</div><div className="font-heading text-2xl font-bold text-white">{tipReport?.count ?? 0}</div></div>
              </div>
              {tipLoading ? (
                <div className="p-12 text-center"><Loader2 className="h-6 w-6 text-gold animate-spin mx-auto" /></div>
              ) : !tipReport || tipReport.items.length === 0 ? (
                <div className="p-12 text-center text-slate-400" data-testid="no-tips">No tips in this range yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="tips-table">
                    <thead>
                      <tr className="text-left text-gold/80 uppercase text-[11px] tracking-wider border-b border-gold/20">
                        <th className="px-6 py-3 font-medium">Date</th>
                        <th className="px-4 py-3 font-medium">Customer</th>
                        <th className="px-4 py-3 font-medium">Booking</th>
                        <th className="px-4 py-3 font-medium">Ref</th>
                        <th className="px-4 py-3 font-medium text-right">Tip</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tipReport.items.map((t, i) => (
                        <tr key={i} className="border-b border-gold/5" data-testid={`tip-row-${i}`}>
                          <td className="px-6 py-3 text-slate-300 whitespace-nowrap">{t.date}</td>
                          <td className="px-4 py-3 text-white">{t.name}</td>
                          <td className="px-4 py-3 text-slate-300 max-w-[280px]">{t.description}</td>
                          <td className="px-4 py-3 text-slate-500">{t.reference}</td>
                          <td className="px-4 py-3 text-right font-semibold text-gold">${t.tip.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="visitors">
            <Visitors data={visitors} loading={visitorLoading} days={visitorDays} live={live} onDays={(d) => { setVisitorDays(d); loadVisitors(d); }} onRefresh={() => loadVisitors()} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function Insights({ stats }) {
  if (!stats) return <div className="p-12 text-center text-slate-400">No data yet.</div>;
  const mc = stats.month_compare || { this_count: 0, this_value: 0, last_count: 0, last_value: 0 };
  const maxDay = Math.max(1, ...(stats.by_day || []).map((d) => d.count));
  const maxSvc = Math.max(1, ...(stats.by_service || []).map((s) => s.count));
  const pay = stats.by_payment || {};
  const payTotal = Math.max(1, (pay.online || 0) + (pay.e_transfer || 0) + (pay.pay_later || 0));
  const payRows = [
    { l: "Pay online (card)", v: pay.online || 0, c: "bg-emerald-400" },
    { l: "Interac e-Transfer", v: pay.e_transfer || 0, c: "bg-gold" },
    { l: "Pay driver on arrival", v: pay.pay_later || 0, c: "bg-sky-400" },
  ];
  const secondary = [
    { icon: Wallet, l: "Total booked value", v: `$${(stats.booked_value ?? 0).toFixed(2)}` },
    { icon: DollarSign, l: "Outstanding balance", v: `$${(stats.outstanding_balance ?? 0).toFixed(2)}`, sub: `${stats.outstanding_count ?? 0} bookings` },
    { icon: Coins, l: "Driver tips collected", v: `$${(stats.tips_total ?? 0).toFixed(2)}` },
    { icon: Send, l: "e-Transfers awaiting", v: stats.awaiting_etransfer_count ?? 0, sub: `$${(stats.awaiting_etransfer_value ?? 0).toFixed(2)}` },
    { icon: CheckCircle2, l: "Completed", v: stats.completed ?? 0 },
    { icon: Clock3, l: "Cancelled", v: stats.cancelled ?? 0 },
  ];

  return (
    <div className="space-y-6" data-testid="insights-panel">
      <div className="grid gap-4 sm:grid-cols-2" data-testid="insights-month-compare">
        {[
          { l: "Bookings", tv: mc.this_count, lv: mc.last_count, money: false },
          { l: "Booked value", tv: mc.this_value, lv: mc.last_value, money: true },
        ].map((m) => {
          const diff = m.tv - m.lv;
          const pct = m.lv > 0 ? Math.round((diff / m.lv) * 100) : (m.tv > 0 ? 100 : 0);
          const up = diff >= 0;
          const fmt = (n) => (m.money ? `$${Number(n).toFixed(2)}` : n);
          return (
            <div key={m.l} className="rounded-sm bg-navy-light border border-gold/20 p-6">
              <div className="flex items-center justify-between">
                <div className="text-sm text-slate-400">{m.l} · this month</div>
                <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${up ? "text-emerald-300 bg-emerald-500/10" : "text-red-300 bg-red-500/10"}`}>
                  {up ? "▲" : "▼"} {Math.abs(pct)}%
                </span>
              </div>
              <div className="font-heading text-3xl font-bold text-white mt-2">{fmt(m.tv)}</div>
              <div className="text-xs text-slate-500 mt-1">Last month: {fmt(m.lv)}</div>
            </div>
          );
        })}
      </div>

      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        {secondary.map((c) => (
          <div key={c.l} className="rounded-sm bg-navy-light border border-gold/20 p-6">
            <c.icon className="h-5 w-5 text-gold mb-3" />
            <div className="font-heading text-2xl font-bold text-white">{c.v}</div>
            <div className="text-sm text-slate-400 mt-1">{c.l}{c.sub ? <span className="text-gold"> · {c.sub}</span> : null}</div>
          </div>
        ))}
      </div>

      <div className="rounded-sm bg-navy-light border border-gold/20 p-6" data-testid="insights-trend">
        <div className="flex items-center gap-2 mb-5"><TrendingUp className="h-5 w-5 text-gold" /><h3 className="font-heading text-lg font-bold text-white">Bookings — last 30 days</h3></div>
        <div className="flex items-end gap-[3px] h-40">
          {(stats.by_day || []).map((d) => (
            <div key={d.date} className="flex-1 group relative flex flex-col justify-end h-full">
              <div className="w-full rounded-t-sm bg-gold/70 hover:bg-gold transition-colors duration-150" style={{ height: `${(d.count / maxDay) * 100}%`, minHeight: d.count ? "4px" : "0" }} />
              <div className="pointer-events-none absolute -top-9 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-navy border border-gold/30 px-2 py-1 text-[11px] text-white opacity-0 group-hover:opacity-100 transition-opacity z-10">{d.date.slice(5)} · {d.count} · ${d.revenue.toFixed(0)}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-sm bg-navy-light border border-gold/20 p-6" data-testid="insights-services">
          <div className="flex items-center gap-2 mb-5"><PieChart className="h-5 w-5 text-gold" /><h3 className="font-heading text-lg font-bold text-white">Most popular services</h3></div>
          <div className="space-y-4">
            {(stats.by_service || []).map((s) => (
              <div key={s.name}>
                <div className="flex justify-between text-sm mb-1"><span className="text-slate-200">{s.name}</span><span className="text-slate-400">{s.count} · <span className="text-gold">${s.revenue.toFixed(0)}</span></span></div>
                <div className="h-2 rounded-full bg-navy overflow-hidden"><div className="h-full bg-gold rounded-full" style={{ width: `${(s.count / maxSvc) * 100}%` }} /></div>
              </div>
            ))}
            {(!stats.by_service || stats.by_service.length === 0) && <div className="text-slate-400 text-sm">No bookings yet.</div>}
          </div>
        </div>

        <div className="rounded-sm bg-navy-light border border-gold/20 p-6" data-testid="insights-payments">
          <div className="flex items-center gap-2 mb-5"><DollarSign className="h-5 w-5 text-gold" /><h3 className="font-heading text-lg font-bold text-white">Payment methods</h3></div>
          <div className="space-y-4">
            {payRows.map((p) => (
              <div key={p.l}>
                <div className="flex justify-between text-sm mb-1"><span className="text-slate-200">{p.l}</span><span className="text-slate-400">{p.v} ({Math.round((p.v / payTotal) * 100)}%)</span></div>
                <div className="h-2 rounded-full bg-navy overflow-hidden"><div className={`h-full rounded-full ${p.c}`} style={{ width: `${(p.v / payTotal) * 100}%` }} /></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}


function BarList({ title, icon: Icon, rows, testid, fmtName }) {
  const max = Math.max(1, ...(rows || []).map((r) => r.count));
  return (
    <div className="rounded-sm bg-navy-light border border-gold/20 p-6" data-testid={testid}>
      <div className="flex items-center gap-2 mb-5"><Icon className="h-5 w-5 text-gold" /><h3 className="font-heading text-lg font-bold text-white">{title}</h3></div>
      <div className="space-y-4">
        {(rows || []).map((r) => (
          <div key={r.name}>
            <div className="flex justify-between text-sm mb-1"><span className="text-slate-200 truncate pr-2">{fmtName ? fmtName(r.name) : r.name}</span><span className="text-gold shrink-0">{r.count}</span></div>
            <div className="h-2 rounded-full bg-navy overflow-hidden"><div className="h-full bg-gold rounded-full" style={{ width: `${(r.count / max) * 100}%` }} /></div>
          </div>
        ))}
        {(!rows || rows.length === 0) && <div className="text-slate-400 text-sm">No visits yet.</div>}
      </div>
    </div>
  );
}

const PAGE_NAMES = {
  "/": "Home", "/services": "Services", "/explore": "Explore", "/about": "About",
  "/contact": "Contact", "/book": "Booking", "/quote": "Request a Quote",
};

function timeAgo(iso) {
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
}

function Visitors({ data, loading, days, live, onDays, onRefresh }) {
  const pageName = (p) => PAGE_NAMES[p] || p;
  const liveBlock = (
    <div className="rounded-sm bg-navy-light border border-emerald-500/25 p-6" data-testid="live-visitors-panel">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Radio className="h-5 w-5 text-emerald-400" />
          <h3 className="font-heading text-lg font-bold text-white">Live right now</h3>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <span className="text-emerald-300 font-semibold" data-testid="live-online-count">{live?.online ?? 0} online</span>
          <span className="text-slate-400">Today: <span className="text-gold font-semibold" data-testid="live-today-count">{live?.today ?? 0}</span></span>
        </div>
      </div>
      <div className="space-y-2" data-testid="live-feed">
        {(live?.recent || []).length === 0 ? (
          <div className="text-slate-400 text-sm">No visitors in the last 30 minutes. You'll get a pop-up here the moment someone lands on the site.</div>
        ) : (
          (live.recent || []).map((v) => (
            <div key={v.id} className="flex items-center justify-between border-b border-white/5 py-2.5 text-sm">
              <div className="flex items-center gap-3 min-w-0">
                <span className="h-2 w-2 rounded-full bg-emerald-400 shrink-0" />
                <span className="text-white truncate">{pageName(v.path)}</span>
                <span className="text-slate-500 text-xs shrink-0">{v.device}{v.source ? ` · ${v.source}` : ""}</span>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-slate-400 text-xs">{v.country && v.country !== "Unknown" ? `${v.city ? v.city + ", " : ""}${v.country}` : "—"}</span>
                <span className="text-slate-500 text-xs w-16 text-right">{timeAgo(v.created_at)}</span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  if (loading && !data) return <div className="space-y-6" data-testid="visitors-panel">{liveBlock}<div className="p-12 text-center text-slate-400"><Loader2 className="h-6 w-6 text-gold animate-spin inline" /></div></div>;
  if (!data) return <div className="space-y-6" data-testid="visitors-panel">{liveBlock}<div className="p-12 text-center text-slate-400">No historical visitor data yet.</div></div>;
  const maxDay = Math.max(1, ...(data.by_day || []).map((d) => d.count));
  const cards = [
    { icon: Globe, l: "Page views", v: data.total, sub: `last ${days} days` },
    { icon: Users, l: "Unique visitors", v: data.unique },
    { icon: Monitor, l: "Top device", v: data.by_device?.[0]?.name || "—" },
    { icon: TrendingUp, l: "Top source", v: data.by_source?.[0]?.name || "Direct" },
  ];
  return (
    <div className="space-y-6" data-testid="visitors-panel">
      {liveBlock}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-slate-400">Daily on-site tracking · in addition to Google Analytics</div>
        <div className="flex items-center gap-2">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => onDays(d)} data-testid={`visitor-days-${d}`}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors duration-200 ${days === d ? "bg-gold text-navy border-gold" : "border-gold/40 text-gold hover:border-gold"}`}>{d}d</button>
          ))}
          <button onClick={onRefresh} data-testid="visitors-refresh" className="inline-flex items-center gap-1.5 rounded-full border border-gold/40 px-3 py-1.5 text-xs font-semibold text-gold hover:bg-gold/10"><RefreshCw className="h-3.5 w-3.5" /> Refresh</button>
        </div>
      </div>

      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        {cards.map((c) => (
          <div key={c.l} className="rounded-sm bg-navy-light border border-gold/20 p-6">
            <c.icon className="h-5 w-5 text-gold mb-3" />
            <div className="font-heading text-2xl font-bold text-white truncate">{c.v}</div>
            <div className="text-sm text-slate-400 mt-1">{c.l}{c.sub ? <span className="text-gold"> · {c.sub}</span> : null}</div>
          </div>
        ))}
      </div>

      <div className="rounded-sm bg-navy-light border border-gold/20 p-6" data-testid="visitors-trend">
        <div className="flex items-center gap-2 mb-5"><TrendingUp className="h-5 w-5 text-gold" /><h3 className="font-heading text-lg font-bold text-white">Visits — last {Math.min(days, 30)} days</h3></div>
        <div className="flex items-end gap-[3px] h-40">
          {(data.by_day || []).map((d) => (
            <div key={d.date} className="flex-1 group relative flex flex-col justify-end h-full">
              <div className="w-full rounded-t-sm bg-gold/70 hover:bg-gold transition-colors duration-150" style={{ height: `${(d.count / maxDay) * 100}%`, minHeight: d.count ? "4px" : "0" }} />
              <div className="pointer-events-none absolute -top-9 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-navy border border-gold/30 px-2 py-1 text-[11px] text-white opacity-0 group-hover:opacity-100 transition-opacity z-10">{d.date.slice(5)} · {d.count} views</div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <BarList title="Top pages" icon={FileText} rows={data.by_page} testid="visitors-pages" fmtName={pageName} />
        <BarList title="Where visitors come from" icon={TrendingUp} rows={data.by_source} testid="visitors-sources" />
        <BarList title="Countries" icon={Globe} rows={data.by_country} testid="visitors-countries" />
        <BarList title="Devices & browsers" icon={Monitor} rows={[...(data.by_device || []), ...(data.by_browser || [])]} testid="visitors-devices" />
      </div>
    </div>
  );
}
